(() => {
  'use strict';
  const element = document.getElementById('sa-analytics-config');
  if (!element || window.SaunaAnalytics) return;
  let config;
  try { config = JSON.parse(element.textContent); } catch { return; }
  const privatePath = /\/(?:account|accounts|customer_authentication|checkouts?|orders|password|gift_cards|challenge)(?:\/|$)|\/pages\/library(?:\/|$)/i;
  const params = new URLSearchParams(location.search);
  if (privatePath.test(location.pathname) || window.Shopify?.designMode || params.has('preview_theme_id') || !/^phc_[A-Za-z0-9]+$/.test(config.token)) return;
  const test = params.get('sa_analytics_test') === '1' || document.cookie.split('; ').some(cookie => cookie === 'sa_posthog_test=1');
  const version = '2026-10-06.1';
  const diagnostic = status => { if (test) document.documentElement.setAttribute('data-sa-analytics-status', status); };
  diagnostic('waiting_for_privacy');
  let sdk;
  let ready = false;
  let loading = false;
  let observer;
  let activeSeconds = 0;
  let lastActivity = Date.now();
  let lastIdentitySync = 0;
  const milestones = new Set();
  const sections = new Set();
  const campaignKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
  const safeText = value => String(value).replace(/[^\s@]+@[^\s@]+\.[^\s@]+/g, '[redacted]').replace(/\b\d{7,}\b/g, '[redacted]').slice(0, 160);
  const safeUrl = value => {
    try {
      const url = new URL(value, location.origin);
      if (!['https:', 'http:'].includes(url.protocol)) return url.protocol;
      return url.origin + (privatePath.test(url.pathname) ? '/[private]' : url.pathname);
    } catch { return ''; }
  };
  const allowed = () => {
    try { return window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true; } catch { return false; }
  };
  const setCookie = (name, value, age) => {
    const domain = /(^|\.)saunaagent\.co$/.test(location.hostname) ? '; Domain=saunaagent.co' : '';
    document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${age}; SameSite=Lax; Secure${domain}`;
  };
  const syncIdentity = () => {
    if (!ready || !allowed()) return;
    setCookie('sa_posthog_id', sdk.get_distinct_id(), 60 * 60 * 24 * 365);
    setCookie('sa_posthog_session', sdk.get_session_id(), 1800);
    setCookie('sa_posthog_test', test ? '1' : '0', 1800);
  };
  const context = {
    integration: 'sauna-agent-shopify', analytics_version: version,
    page_type: config.page_type, page_handle: config.page_handle || '',
    locale: config.locale, currency: config.currency, analytics_test: test,
    environment: 'production'
  };
  function sanitize(value, key = '', depth = 0) {
    if (depth > 12) return undefined;
    if (typeof value === 'string') {
      if (/url|referrer|href/i.test(key)) return safeUrl(value);
      if (/utm_|campaign|search_term/i.test(key)) return safeText(value);
      return value;
    }
    if (Array.isArray(value)) return value.map(item => sanitize(item, key, depth + 1));
    if (value && typeof value === 'object') {
      const clean = {};
      for (const [name, item] of Object.entries(value)) {
        if (/^(?:email|phone|name|first_name|last_name|address|customer|contact|token|password|\$search_term)$/i.test(name)) continue;
        clean[name] = sanitize(item, name, depth + 1);
      }
      return clean;
    }
    return value;
  }
  function capture(name, properties = {}) {
    if (!ready || !allowed()) return false;
    try {
      syncIdentity();
      sdk.capture(name, { ...context, ...sanitize(properties) });
      return true;
    } catch { return false; }
  }
  window.SaunaAnalytics = { capture, get ready() { return ready && allowed(); } };
  function trackPage() {
    capture('$pageview', { $current_url: safeUrl(location.href), $pathname: location.pathname });
    if (config.product) capture('product_viewed', config.product);
    if (config.search_results_count !== null) capture('search_results_viewed', { results_count: config.search_results_count });
    if (config.page_type === '404') capture('page_not_found');
    if (config.page_type === 'collection') capture('collection_viewed', { collection_handle: config.page_handle });
    observer?.disconnect();
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(entries => {
        for (const entry of entries) {
          if (!ready || !allowed() || !entry.isIntersecting || sections.has(entry.target.id)) continue;
          sections.add(entry.target.id);
          capture('section_viewed', { section_id: entry.target.id });
          observer.unobserve(entry.target);
        }
      }, { threshold: 0.15 });
      document.querySelectorAll('main section[id], main .sa-faq-topic[id]').forEach(section => observer.observe(section));
    }
    document.dispatchEvent(new CustomEvent('sa:analytics-ready'));
  }
  function activate() {
    if (!allowed() || ready) return;
    try {
    sdk.opt_in_capturing({ captureEventName: false });
    sdk.register(context);
    const campaigns = {};
    campaignKeys.forEach(key => { if (params.has(key)) campaigns[key] = safeText(params.get(key)); });
    sdk.register(campaigns);
    ready = true;
    diagnostic('ready');
    lastActivity = Date.now();
    activeSeconds = 0;
    milestones.clear();
    sections.clear();
    syncIdentity();
    if (config.replay && config.page_type !== 'search') sdk.startSessionRecording();
    trackPage();
    } catch { stop(); }
  }
  function stop() {
    ready = false;
    diagnostic('consent_denied');
    observer?.disconnect();
    if (sdk) {
      try { sdk.stopSessionRecording(); } catch {}
      try { sdk.opt_out_capturing({ clear_persistence: true }); } catch {}
    }
    ['sa_posthog_id', 'sa_posthog_session', 'sa_posthog_test'].forEach(name => setCookie(name, '', 0));
    document.dispatchEvent(new CustomEvent('sa:analytics-stopped'));
  }
  function initialize() {
    if (!allowed()) { diagnostic('consent_denied'); if (ready || sdk) stop(); return; }
    if (sdk) { activate(); return; }
    if (loading) return;
    loading = true;
    diagnostic('loading_sdk');
    const script = document.createElement('script');
    script.src = 'https://eu-assets.i.posthog.com/static/array.js';
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.onerror = () => { loading = false; diagnostic('sdk_load_failed'); };
    script.onload = () => {
      loading = false;
      if (!allowed()) { diagnostic('consent_denied'); return; }
      if (!window.posthog?.init) { diagnostic('sdk_init_missing'); return; }
      try {
      window.posthog.init(config.token, {
        api_host: 'https://eu.i.posthog.com', ui_host: 'https://eu.posthog.com', defaults: '2026-05-30',
        capture_pageview: false, capture_pageleave: true,
        autocapture: config.page_type === 'search' ? false : {
          dom_event_allowlist: ['click', 'change', 'submit'],
          css_selector_ignorelist: ['.ph-no-autocapture', '[data-ph-no-autocapture]', 'input', 'textarea', '[contenteditable]', '[data-private]'],
          capture_copied_text: false
        },
        mask_all_text: true, mask_all_element_attributes: true,
        capture_heatmaps: true, capture_dead_clicks: true, rageclick: true,
        capture_performance: { web_vitals: true }, capture_exceptions: false,
        enable_recording_console_log: false, disable_surveys: true,
        person_profiles: 'identified_only', cookie_expiration: 365,
        disable_session_recording: true,
        session_recording: {
          maskAllInputs: true,
          blockSelector: '[data-private], .ph-no-capture, form[action*="/contact"], iframe',
          maskTextSelector: '[role="alert"], [contenteditable]',
          recordBody: false, recordHeaders: false, recordCanvas: false,
          streamNetworkBody: false, captureJsonLd: false,
          maskCapturedNetworkRequestFn: request => ({ ...request, name: safeUrl(request.name), requestBody: undefined, responseBody: undefined, requestHeaders: undefined, responseHeaders: undefined })
        },
        before_send: event => {
          if (!allowed()) return null;
          // The SDK stores the public ingestion key in properties.token.
          // Restore only our configured key after removing arbitrary tokens.
          if (event.event !== '$snapshot') event.properties = { ...sanitize(event.properties), token: config.token };
          return event;
        },
        loaded: instance => { sdk = instance; activate(); }
      });
      } catch { stop(); }
    };
    document.head.appendChild(script);
  }
  document.addEventListener('visitorConsentCollected', initialize);
  window.addEventListener('pageshow', initialize);
  let attempts = 0;
  function loadPrivacy() {
    if (typeof window.Shopify?.customerPrivacy?.analyticsProcessingAllowed === 'function') { initialize(); return; }
    if (window.Shopify?.loadFeatures) {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], error => { if (!error) initialize(); });
    } else if (++attempts < 40) setTimeout(loadPrivacy, 250);
  }
  loadPrivacy();
  ['pointerdown', 'keydown', 'scroll'].forEach(name => window.addEventListener(name, () => {
    lastActivity = Date.now();
    if (!ready || !allowed()) return;
    if (Date.now() - lastIdentitySync > 10000) { try { syncIdentity(); } catch {} lastIdentitySync = Date.now(); }
    if (name !== 'scroll') return;
    const height = document.documentElement.scrollHeight - window.innerHeight;
    const depth = height > 0 ? Math.min(100, Math.round(window.scrollY / height * 100)) : 100;
    [25, 50, 75, 90, 100].forEach(mark => {
      if (depth >= mark && !milestones.has(`scroll-${mark}`)) {
        milestones.add(`scroll-${mark}`);
        capture('scroll_depth', { percent: mark });
      }
    });
  }, { passive: true }));
  setInterval(() => {
    if (!ready || !allowed() || document.visibilityState !== 'visible' || Date.now() - lastActivity > 30000) return;
    activeSeconds += 5;
    if ([15, 30, 60, 120, 300].includes(activeSeconds)) capture('engaged_time', { active_seconds: activeSeconds });
  }, 5000);
  window.addEventListener('error', event => {
    if (!event.filename || !event.lineno) return;
    capture('storefront_error', { error_type: 'javascript_error', script_url: safeUrl(event.filename), line: event.lineno, column: event.colno });
  });
  window.addEventListener('unhandledrejection', () => capture('storefront_error', { error_type: 'unhandled_promise_rejection' }));
})();
